import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * The role a signed-in person has chosen to act as.
 *
 * `player` is not an app role and nothing issues it: everybody who can sign in is a player, so it
 * is the baseline rather than an entitlement. The other two are the Entra app roles, spelled the
 * way the token spells them.
 */
export type ActingRole = 'admin' | 'room' | 'player';

export const ACTING_ROLES: readonly ActingRole[] = ['admin', 'room', 'player'];

/**
 * What each role is called on screen. One place, because the chooser, the switcher and every
 * refusal message have to agree — "Operator" and "Room operator" in two corners of the same
 * screen read as two different things.
 */
export const ACTING_ROLE_LABELS: Record<ActingRole, string> = {
  admin: 'Platform administrator',
  room: 'Room operator',
  player: 'Player',
};

/** Short forms, for the switcher in a header where there is no room for the full label. */
export const ACTING_ROLE_SHORT_LABELS: Record<ActingRole, string> = {
  admin: 'Admin',
  room: 'Operator',
  player: 'Player',
};

/**
 * Session storage, not local storage. The choice is scoped to this browsing session on purpose:
 * it is closer to "who am I being right now" than to a preference, and an operator who closes the
 * tab and comes back should land in the app rather than in whatever role they last used to check
 * something. It survives a reload, which is the case that actually matters — a reload in the
 * middle of a task must not silently change what the person can do.
 *
 * Each app is its own origin, so room-web's choice and player-web's are separate by construction
 * and can share this key.
 */
const STORAGE_KEY = 'brush.acting-role';

function isActingRole(value: string | null): value is ActingRole {
  return value !== null && (ACTING_ROLES as readonly string[]).includes(value);
}

/**
 * The stored choice, or null if none has been made.
 *
 * Free of React on purpose: the API client reads it on every request, and reading storage each
 * time is what keeps a switch from being fought by a stale closure captured when the client was
 * built. Storage can throw (a locked-down browser, a private window), so it never does.
 */
export function readActingRole(): ActingRole | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return isActingRole(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function writeActingRole(role: ActingRole): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, role);
  } catch {
    // A choice that cannot be stored still applies to this page: the provider holds it in state.
    // It is lost on reload, which is worse than persisting it and better than refusing to switch.
  }
}

export function clearStoredActingRole(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if it could never be written.
  }
}

interface ActingRoleContextValue {
  /** The chosen role, or null while the person has not chosen one. */
  actingRole: ActingRole | null;
  choose: (role: ActingRole) => void;
  /** Forget the choice — used when the server refuses it, and by "switch role". */
  clear: () => void;
}

const ActingRoleContext = createContext<ActingRoleContextValue | null>(null);

export function useActingRole(): ActingRoleContextValue {
  const ctx = useContext(ActingRoleContext);
  if (!ctx) throw new Error('useActingRole must be used within an ActingRoleProvider.');
  return ctx;
}

/**
 * Holds the chosen role for rendering. The *authoritative* copy is session storage, which is what
 * the API client reads; this mirrors it so that choosing re-renders.
 *
 * Initialised from storage once, at mount, for the same reason room-web's default room is: a
 * change made in another tab must not move the role out from under whatever is on screen here.
 */
export function ActingRoleProvider({ children }: { children: ReactNode }) {
  const [actingRole, setActingRole] = useState<ActingRole | null>(() => readActingRole());

  const choose = useCallback((role: ActingRole) => {
    writeActingRole(role);
    setActingRole(role);
  }, []);

  const clear = useCallback(() => {
    clearStoredActingRole();
    setActingRole(null);
  }, []);

  const value = useMemo<ActingRoleContextValue>(
    () => ({ actingRole, choose, clear }),
    [actingRole, choose, clear],
  );

  return <ActingRoleContext.Provider value={value}>{children}</ActingRoleContext.Provider>;
}

/**
 * The roles this account may act as *in this app*, in the order they are offered.
 *
 * The two lists are not the same question and must not be collapsed. `available` is what the
 * server says the account holds — admin, room, player — and `supported` is what this app can do
 * anything with. player-web supports all three; room-web supports the two staff roles, because
 * choosing "player" there would lock somebody out of the app they just opened; admin-web supports
 * only admin and offers no chooser at all.
 */
export function offerableRoles(
  available: readonly string[],
  supported: readonly ActingRole[],
): ActingRole[] {
  return supported.filter((role) => available.includes(role));
}
